import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Award, Lightbulb, CheckCircle2, Building2, Home as HomeIcon, Trees, Search, PenTool, HardHat, KeyRound } from 'lucide-react';
import ScrollReveal from '../components/ScrollReveal';
import { projectsData } from '../projectsData';
import useScrollIlluminate from '../hooks/useScrollIlluminate';
import './Home.css';
import SEO, { seoConfig } from '../components/SEO';

// Étapes de « Notre accompagnement » : les libellés restent dans translations.js,
// seule l'icône est définie ici.
const PROCESS_STEPS = [
  { key: 'step1', icon: <Search size={22} strokeWidth={1.6} /> },
  { key: 'step2', icon: <PenTool size={22} strokeWidth={1.6} /> },
  { key: 'step3', icon: <HardHat size={22} strokeWidth={1.6} /> },
  { key: 'step4', icon: <KeyRound size={22} strokeWidth={1.6} /> },
];

// Grilles animées au scroll (référence stable, hors composant).
const ILLUMINATE_GROUPS = [
  ['.process-timeline', '.process-step', '.process-line, .process-line-v'],
];

export default function Home({ t, language }) {
  const rootRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [visibleProjects, setVisibleProjects] = useState(3); // Nombre de projets visibles

  // Détecte la taille d'écran
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setVisibleProjects(1); // Mobile : 1 projet
      } else {
        setVisibleProjects(3); // Desktop/Tablette : 3 projets
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-carousel pour les projets (défile automatiquement)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % Math.min(6, projectsData.length));
    }, 4500);
    return () => clearInterval(interval);
  }, []);

  const scrollToSection = (id) => {
    const section = document.getElementById(id);
    if (section) {
      const yOffset = -85;
      const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const featuredProjects = projectsData.slice(0, 6);

  // Illumination séquentielle des étapes + tracé du fil conducteur.
  useScrollIlluminate(rootRef, ILLUMINATE_GROUPS, [language]);

  return (
    <div className="home" ref={rootRef}>
      {/* SEO Component */}
      <SEO 
        title={seoConfig.home.title}
        description={seoConfig.home.description}
        keywords={seoConfig.home.keywords}
      />
      
      {/* ========== HERO SECTION ========== */}
      <section className="hero">
        <div
          className="hero-background"
          style={{
            backgroundImage: `url(/images/projets/pierre-vives/esquisse.webp)`
          }}
        >
          <div className="hero-overlay" />
        </div>
        
        <div className="hero-content">
          <h1 className="hero-title">{t.home.title}</h1>
          <p className="hero-subtitle">{t.home.subtitle}</p>
          
          <div className="hero-cta-group">
            <Link to="/contact" className="hero-cta hero-cta-primary">
              <span>{t.home.startProject}</span>
            </Link>
            <Link to="/projects" className="hero-cta hero-cta-secondary">
              <span>{t.home.discover}</span>
            </Link>
          </div>
        </div>

        <button 
          onClick={() => scrollToSection('expertise')}
          className="scroll-indicator"
          aria-label={t.home.discover}
        >
          <ChevronDown size={32} />
        </button>
      </section>

      {/* ========== SECTION EXPERTISE / USP ========== */}
      <section id="expertise" className="expertise-section">
        <div className="section-container">
          <ScrollReveal>
            <div className="expertise-header">
              <h2 className="section-title">{t.home.expertise.title}</h2>
              <p className="section-intro">{t.home.expertise.intro}</p>
            </div>
          </ScrollReveal>

          <div className="usp-grid">
            <ScrollReveal delay={0.1}>
              <div className="usp-card">
                <div className="usp-icon">
                  <Award size={32} strokeWidth={1.5} />
                </div>
                <h3 className="usp-title">{t.home.expertise.usp1.title}</h3>
                <p className="usp-text">{t.home.expertise.usp1.text}</p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <div className="usp-card">
                <div className="usp-icon">
                  <Lightbulb size={32} strokeWidth={1.5} />
                </div>
                <h3 className="usp-title">{t.home.expertise.usp2.title}</h3>
                <p className="usp-text">{t.home.expertise.usp2.text}</p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.3}>
              <div className="usp-card">
                <div className="usp-icon">
                  <CheckCircle2 size={32} strokeWidth={1.5} />
                </div>
                <h3 className="usp-title">{t.home.expertise.usp3.title}</h3>
                <p className="usp-text">{t.home.expertise.usp3.text}</p>
              </div>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={0.4}>
            <div className="expertise-cta">
              <Link to="/agency" className="btn-secondary">
                {t.home.expertise.cta}
                {/* <ChevronRight size={20} /> */}
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ========== PROJETS PHARES - CARROUSEL RESPONSIVE ========== */}
      <section className="featured-projects-section">
        <div className="section-container">
          <ScrollReveal>
            <div className="featured-header">
              <h2 className="section-title">{t.home.featured.title}</h2>
              <p className="section-intro">{t.home.featured.intro}</p>
            </div>
          </ScrollReveal>

          <div className="featured-carousel">
            <div className="featured-grid">
              {Array.from({ length: visibleProjects }).map((_, offset) => {
                const project = featuredProjects[(currentSlide + offset) % featuredProjects.length];
                return (
                  <ScrollReveal key={`${project.id}-${currentSlide}-${offset}`} delay={offset * 0.1}>
                    <Link to={`/project/${project.id}`} className="featured-card">
                      <div className="featured-image-wrapper">
                        <img
                          src={project.image}
                          alt={`${project.title} - ${project.location} | Architecture Montpellier`}
                          className="featured-image"
                          loading='lazy'
                        />
                        <div className="featured-overlay">
                          <div className="featured-badge">{t.home.featured.viewProject}</div>
                        </div>
                      </div>
                      <div className="featured-content">
                        <h3 className="featured-title">{project.title}</h3>
                        <p className="featured-location">{project.location}</p>
                      </div>
                    </Link>
                  </ScrollReveal>
                );
              })}
            </div>
          </div>

          <ScrollReveal delay={0.4}>
            <div className="featured-cta">
              <Link to="/projects" className="btn-primary">
                {t.home.featured.cta}
                {/* <ArrowRight size={20} /> */}
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ========== PROCESSUS / COMMENT NOUS TRAVAILLONS ========== */}
      <section className="process-section">
        <div className="section-container">
          <ScrollReveal>
            <h2 className="section-title">{t.home.process.title}</h2>
            <p className="section-intro">{t.home.process.intro}</p>
          </ScrollReveal>

          <div className="process-track">
            <svg
              className="process-line"
              viewBox="0 0 1200 110"
              preserveAspectRatio="none"
              aria-hidden="true"
              focusable="false"
            >
              <path
                className="process-line-base"
                d="M0,46 C140,28 240,28 380,46 C520,64 620,64 760,46 C880,31 1020,46 1150,46 L1183,46"
              />
              {/* flèche au repos : grise, comme le rail */}
              <path className="process-line-arrowhead-base" d="M1183,38 L1203,46 L1183,54 Z" />
              <path
                className="process-line-glow"
                d="M0,46 C140,28 240,28 380,46 C520,64 620,64 760,46 C880,31 1020,46 1150,46 L1183,46"
              />
              <path className="process-line-arrowhead" d="M1183,38 L1203,46 L1183,54 Z" />
            </svg>

            {/* Petit écran : les cartes s'empilent, le fil passe à la verticale */}
            <svg
              className="process-line-v"
              viewBox="0 0 56 1000"
              preserveAspectRatio="none"
              aria-hidden="true"
              focusable="false"
            >
              <path
                className="process-line-base"
                d="M28,0 C16,120 40,200 28,320 C16,440 40,520 28,640 C16,760 40,840 28,960 L28,975"
              />
              <path className="process-line-arrowhead-base" d="M20,975 L28,995 L36,975 Z" />
              <path
                className="process-line-glow"
                d="M28,0 C16,120 40,200 28,320 C16,440 40,520 28,640 C16,760 40,840 28,960 L28,975"
              />
              <path className="process-line-arrowhead process-line-arrowhead--v" d="M20,975 L28,995 L36,975 Z" />
            </svg>

            <div className="process-timeline">
              {PROCESS_STEPS.map((step, idx) => (
                <div className="process-step" key={step.key}>
                  <div className="process-icon">{step.icon}</div>
                  <span className="process-num">{String(idx + 1).padStart(2, '0')}</span>
                  <h3 className="process-title">{t.home.process[step.key].title}</h3>
                  <p className="process-text">{t.home.process[step.key].text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== NOS DOMAINES D'INTERVENTION ========== */}
      <section className="services-section">
        <div className="section-container">
          <ScrollReveal>
            <h2 className="section-title">{t.home.services.title}</h2>
          </ScrollReveal>

          <div className="services-grid">
            <ScrollReveal delay={0.1}>
              <Link to="/missions" className="service-card">
                <div className="service-icon-modern">
                  <Building2 size={36} strokeWidth={1.5} />
                </div>
                <h3 className="service-title">{t.home.services.collective.title}</h3>
                <p className="service-text">{t.home.services.collective.text}</p>
                <div className="service-arrow">
                  <ChevronRight size={20} />
                </div>
              </Link>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <Link to="/missions" className="service-card">
                <div className="service-icon-modern">
                  <HomeIcon size={36} strokeWidth={1.5} />
                </div>
                <h3 className="service-title">{t.home.services.individual.title}</h3>
                <p className="service-text">{t.home.services.individual.text}</p>
                <div className="service-arrow">
                  <ChevronRight size={20} />
                </div>
              </Link>
            </ScrollReveal>

            <ScrollReveal delay={0.3}>
              <Link to="/missions" className="service-card">
                <div className="service-icon-modern">
                  <Trees size={36} strokeWidth={1.5} />
                </div>
                <h3 className="service-title">{t.home.services.territory.title}</h3>
                <p className="service-text">{t.home.services.territory.text}</p>
                <div className="service-arrow">
                  <ChevronRight size={20} />
                </div>
              </Link>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ========== CTA FINALE ========== */}
      <section className="cta-final-section">
        <div className="section-container">
          <ScrollReveal>
            <div className="cta-final-content">
              <h2 className="cta-final-title">{t.home.ctaFinal.title}</h2>
              <p className="cta-final-text">{t.home.ctaFinal.text}</p>
              <div className="cta-final-buttons">
                <Link to="/contact" className="btn-primary-large">
                  {t.home.ctaFinal.contact}
                </Link>
                <Link to="/projects" className="btn-outline-large">
                  {t.home.ctaFinal.projects}
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}